import React, { useState, useEffect } from 'react';
import { billingService } from '../../services/billingService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Container
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BarChart as BarChartIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const EncodingStats = ({ onCustomerClick }) => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [encodingStats, setEncodingStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [filterBarangay, setFilterBarangay] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('Active');
  
  // Expanded state for encoded/not-encoded per barangay
  const [expandedEncoded, setExpandedEncoded] = useState([]);
  const [expandedNotEncoded, setExpandedNotEncoded] = useState([]);

  useEffect(() => {
    setSelectedMonth(getCurrentMonth());
  }, []);

  const getCurrentMonth = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  };

  const loadEncodingStats = async () => {
    if (!selectedMonth) return;
    
    setLoading(true);
    setError('');
    const billedMonth = `${selectedMonth}-01`;
    const result = await billingService.getEncodingStats(billedMonth);
    
    if (result.success) {
      setEncodingStats(result.data);
      applyFilters(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const applyFilters = (data = encodingStats) => {
    let filtered = [...data];
    
    if (filterBarangay) {
      // Case-insensitive barangay filter
      filtered = filtered.filter(stat => 
        stat.barangay?.toLowerCase() === filterBarangay.toLowerCase()
      );
    }
    
    if (filterType || filterStatus) {
      filtered = filtered.map(stat => {
        // Filter ALL customers based on type/status (case-insensitive)
        const filteredAllCustomers = (stat.allCustomers || []).filter(customer => {
          const matchesType = !filterType || customer.type?.toLowerCase() === filterType.toLowerCase();
          const matchesStatus = !filterStatus || customer.status?.toLowerCase() === filterStatus.toLowerCase();
          return matchesType && matchesStatus;
        });
        
        // Recalculate counts based on filtered customers
        const filteredEncodedCount = filteredAllCustomers.filter(c => c.isEncoded).length;
        const filteredNotEncodedCustomers = filteredAllCustomers.filter(c => !c.isEncoded);
        
        return {
          ...stat,
          totalCustomers: filteredAllCustomers.length,
          encodedCount: filteredEncodedCount,
          notEncodedCustomers: filteredNotEncodedCustomers
        };
      });
    }
    
    setFilteredStats(filtered);
  };

  const handleClearFilters = () => {
    setFilterBarangay('');
    setFilterType('');
    setFilterStatus('');
    setFilteredStats(encodingStats);
  };

  const toggleEncodedExpanded = (barangay) => {
    setExpandedEncoded(prev => 
      prev.includes(barangay)
        ? prev.filter(b => b !== barangay)
        : [...prev, barangay]
    );
  };

  const toggleNotEncodedExpanded = (barangay) => {
    setExpandedNotEncoded(prev => 
      prev.includes(barangay)
        ? prev.filter(b => b !== barangay)
        : [...prev, barangay]
    );
  };

  useEffect(() => {
    if (encodingStats.length > 0) {
      applyFilters();
    }
  }, [filterBarangay, filterType, filterStatus, encodingStats]);

  const handleExportExcel = () => {
    const headers = ['Barangay', 'Total Customers', 'Encoded', 'Not Encoded', 'Not Encoded List'];
    const rows = encodingStats.map(stat => [
      stat.barangay,
      stat.totalCustomers,
      stat.encodedCount,
      stat.notEncodedCustomers.length,
      stat.notEncodedCustomers.map(c => `${c.customerid}: ${c.name}`).join('; ')
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `encoding_stats_${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Encoding Stats Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #333; }
          h2 { color: #666; margin-top: 30px; border-bottom: 2px solid #ddd; padding-bottom: 5px; }
          h3 { color: #888; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-radius: 5px; }
          .stat-box { display: inline-block; padding: 10px 15px; margin: 5px; background: #e8f4f8; border-radius: 5px; }
          .no-print { margin-top: 30px; text-align: center; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>📊 Billing Encoding Statistics Report</h1>
        <div class="summary">
          <p><strong>Report Period:</strong> ${new Date(selectedMonth + '-01').toLocaleDateString('en-PH', { year: 'numeric', month: 'long' })}</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleString('en-PH')}</p>
          <p><strong>Total Barangays:</strong> ${encodingStats.length}</p>
        </div>
        
        ${encodingStats.map(stat => `
          <h2>🏘️ ${stat.barangay}</h2>
          <div style="margin: 10px 0;">
            <div class="stat-box">
              <strong>Total Customers:</strong> ${stat.totalCustomers}
            </div>
            <div class="stat-box" style="background: #d4edda;">
              <strong>Encoded Bills:</strong> ${stat.encodedCount}
            </div>
            <div class="stat-box" style="background: ${stat.notEncodedCustomers.length > 0 ? '#f8d7da' : '#d4edda'};">
              <strong>Not Encoded:</strong> ${stat.notEncodedCustomers.length}
            </div>
            <div class="stat-box" style="background: #d1ecf1;">
              <strong>Completion:</strong> ${((stat.encodedCount / stat.totalCustomers) * 100).toFixed(1)}%
            </div>
          </div>
          
          ${stat.notEncodedCustomers.length > 0 ? `
            <h3>⚠️ Customers Without Bills (${stat.notEncodedCustomers.length})</h3>
            <table>
              <thead>
                <tr>
                  <th style="width: 20%;">Customer ID</th>
                  <th>Customer Name</th>
                </tr>
              </thead>
              <tbody>
                ${stat.notEncodedCustomers.map(c => `
                  <tr>
                    <td><strong>${c.customerid}</strong></td>
                    <td>${c.name}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p style="color: #28a745; font-weight: bold;">✅ All customers in this barangay have been encoded!</p>'}
        `).join('')}
        
        <div class="summary" style="margin-top: 40px;">
          <h3>📈 Overall Summary</h3>
          <p><strong>Total Customers:</strong> ${encodingStats.reduce((sum, s) => sum + s.totalCustomers, 0)}</p>
          <p><strong>Total Encoded:</strong> ${encodingStats.reduce((sum, s) => sum + s.encodedCount, 0)}</p>
          <p><strong>Total Not Encoded:</strong> ${encodingStats.reduce((sum, s) => sum + s.notEncodedCustomers.length, 0)}</p>
          <p><strong>Overall Completion:</strong> ${(
            (encodingStats.reduce((sum, s) => sum + s.encodedCount, 0) / 
            encodingStats.reduce((sum, s) => sum + s.totalCustomers, 0)) * 100
          ).toFixed(1)}%</p>
        </div>
        
        <div class="no-print">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #007bff; color: white; border: none; border-radius: 5px; margin-right: 10px;">
            🖨️ Print Report
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #6c757d; color: white; border: none; border-radius: 5px;">
            ❌ Close
          </button>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <Container maxWidth="xl" sx={{ p: 0 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Month Selector and Actions */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                size="small"
                sx={{ width: 200 }}
              />
              <Button 
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={loadEncodingStats}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load Stats'}
              </Button>
            </Stack>
            
            {encodingStats.length > 0 && (
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  startIcon={<FileDownloadIcon />} 
                  onClick={handleExportExcel}
                >
                  Export
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<PrintIcon />} 
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      {encodingStats.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
              <FilterListIcon fontSize="small" /> Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Barangay</InputLabel>
                  <Select 
                    value={filterBarangay} 
                    label="Barangay" 
                    onChange={(e) => setFilterBarangay(e.target.value)}
                  >
                    <MenuItem value="">All Barangays</MenuItem>
                    {encodingStats.map((stat) => (
                      <MenuItem key={stat.barangay} value={stat.barangay}>{stat.barangay}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select 
                    value={filterType} 
                    label="Type" 
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Residential">Residential</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Industrial">Industrial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select 
                    value={filterStatus} 
                    label="Status" 
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Disconnected">Disconnected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button 
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  disabled={!filterBarangay && !filterType && !filterStatus}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && encodingStats.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed divider' }}>
          <BarChartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a month and click "Load Stats" to view encoding statistics
          </Typography>
        </Box>
      )}

      {/* Stats Content */}
      {filteredStats.length > 0 && (
        <>
          {/* Overall Summary */}
          <Card variant="outlined" sx={{ mb: 4, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>Total Customers</Typography>
                    <Typography variant="h4" fontWeight="bold">{filteredStats.reduce((sum, s) => sum + s.totalCustomers, 0)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold', opacity: 0.9 }}>Encoded Bills</Typography>
                    <Typography variant="h4" fontWeight="bold">{filteredStats.reduce((sum, s) => sum + s.encodedCount, 0)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold', opacity: 0.9 }}>Not Encoded</Typography>
                    <Typography variant="h4" fontWeight="bold">{filteredStats.reduce((sum, s) => sum + s.notEncodedCustomers.length, 0)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold', opacity: 0.9 }}>Completion Rate</Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {filteredStats.reduce((sum, s) => sum + s.totalCustomers, 0) > 0 ? (
                        (filteredStats.reduce((sum, s) => sum + s.encodedCount, 0) / 
                        filteredStats.reduce((sum, s) => sum + s.totalCustomers, 0)) * 100
                      ).toFixed(1) : 0}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Per Barangay Stats */}
          <Stack spacing={3}>
            {filteredStats.map((stat) => {
              const encodedCustomers = (stat.allCustomers || []).filter(c => c.isEncoded);
              
              return (
                <Card key={stat.barangay} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">{stat.barangay}</Typography>
                    </Box>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Total</Typography>
                          <Typography variant="h5" fontWeight="bold">{stat.totalCustomers}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="caption" color="success.main" fontWeight="bold">Encoded</Typography>
                            <Typography variant="h5" fontWeight="bold" color="success.dark">{stat.encodedCount}</Typography>
                          </Box>
                          {stat.encodedCount > 0 && (
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => toggleEncodedExpanded(stat.barangay)}
                            >
                              {expandedEncoded.includes(stat.barangay) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="caption" color="error.main" fontWeight="bold">Pending</Typography>
                            <Typography variant="h5" fontWeight="bold" color="error.dark">{stat.notEncodedCustomers.length}</Typography>
                          </Box>
                          {stat.notEncodedCustomers.length > 0 && (
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => toggleNotEncodedExpanded(stat.barangay)}
                            >
                              {expandedNotEncoded.includes(stat.barangay) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="info.main" fontWeight="bold">Progress</Typography>
                          <Typography variant="h5" fontWeight="bold" color="info.dark">
                            {((stat.encodedCount / stat.totalCustomers) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Expandable Encoded Customer List */}
                    <Collapse in={expandedEncoded.includes(stat.barangay)}>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" color="success.main" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
                          Encoded Customers ({stat.encodedCount})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Customer ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {encodedCustomers.map((customer) => (
                                <TableRow 
                                  key={customer.customerid}
                                  hover
                                  onClick={() => onCustomerClick && onCustomerClick(customer)}
                                  sx={{ cursor: onCustomerClick ? 'pointer' : 'default' }}
                                >
                                  <TableCell sx={{ fontWeight: 'bold' }}>{customer.customerid}</TableCell>
                                  <TableCell>{customer.name}</TableCell>
                                  <TableCell><Chip label={customer.type} size="small" variant="outlined" /></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={customer.status} 
                                      size="small" 
                                      color={customer.status === 'Active' ? 'success' : 'warning'} 
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Collapse>

                    {/* Expandable Not Encoded Customer List */}
                    <Collapse in={expandedNotEncoded.includes(stat.barangay)}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <WarningIcon fontSize="small" sx={{ mr: 1 }} />
                          Customers Without Bills ({stat.notEncodedCustomers.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Customer ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {stat.notEncodedCustomers.map((customer) => (
                                <TableRow 
                                  key={customer.customerid}
                                  hover
                                  onClick={() => onCustomerClick && onCustomerClick(customer)}
                                  sx={{ cursor: onCustomerClick ? 'pointer' : 'default' }}
                                >
                                  <TableCell sx={{ fontWeight: 'bold' }}>{customer.customerid}</TableCell>
                                  <TableCell>{customer.name}</TableCell>
                                  <TableCell><Chip label={customer.type} size="small" variant="outlined" /></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={customer.status} 
                                      size="small" 
                                      color={customer.status === 'Active' ? 'success' : 'warning'} 
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Collapse>

                    {stat.notEncodedCustomers.length === 0 && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        All customers in this barangay have been encoded!
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </>
      )}
    </Container>
  );
};

export default EncodingStats;
